import React, { useState, useEffect, useCallback } from 'react';
import { formatBalance, balanceToneClass } from '../../../lib/helpers/balance';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomModal from '../../Elements/CustomModal';
import { BsCart4 } from "react-icons/bs";
import { MdCreditCard } from "react-icons/md";
import { FaEdit } from "react-icons/fa";
import { FaDeleteLeft } from "react-icons/fa6";
import { HiOutlineClock } from "react-icons/hi";
import ClientTransactions from "../DispenserComponents/ClientTransactions";
import WalletStatement from "../CustomerComponents/WalletStatement";
import HistorialPestanas from "../../Elements/HistorialPestanas";
import { getImageCacheKey, getImageUrl, socioNum } from '../../../lib/helpers';

function StaffOwnerDetailModal({ active, handleModal, member, token, venueID, onEdit, onDelete, onAddCredit, isMemberInVenue = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isInDispenser = location.pathname.includes('/dispenser');
  const [showHistory, setShowHistory] = useState(false);
  // El personal también tiene saldo (Profile unificado): Monedero como en la
  // ficha del socio (reporte de Yago, 12-10-2026).
  const [historyTab, setHistoryTab] = useState('movimientos');
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Función para obtener las transacciones - envuelta en useCallback
  const fetchTransactions = useCallback(async () => {
    if (!token || !member?.id) {
      return;
    }
    
    setLoadingTransactions(true);
    try {
      // Solo las de esta persona (el filtro de abajo se queda como red):
      // pedir el club entero por modal era parte de la lentitud (Sentry 20-ago).
      const response = await fetch(`/v1/get-all-transactions?user_id=${member.id}&limit=200`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Error al obtener transacciones');
      }
      
      const data = await response.json();
      const txList = Array.isArray(data.transactions) ? data.transactions : (Array.isArray(data) ? data : []);
      setTransactions(txList);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  }, [token, member?.id]);

  // Obtener transacciones cuando se muestra el historial
  useEffect(() => {
    if (showHistory && active && member?.id) {
      fetchTransactions();
    }
  }, [showHistory, active, member?.id, fetchTransactions]);

  // Otra persona en el mismo modal: vuelve a Movimientos (igual que ClientDetailModal).
  useEffect(() => {
    setHistoryTab('movimientos');
  }, [member?.id]);

  // Resetear estado cuando se cierra el modal
  useEffect(() => {
    if (!active) {
      setShowHistory(false);
      setHistoryTab('movimientos');
      setTransactions([]);
    }
  }, [active]);

  if (!active || !member) return null;

  const isOwner = member.userType === 'owner';

  const imageUrl = getImageUrl(member.profile?.documents_path, {
    cacheKey: getImageCacheKey(isOwner ? 'owner' : 'staff', member.id),
  });

  const displayName = member.profile?.first_name && member.profile?.last_name
    ? `${member.profile.first_name} ${member.profile.last_name}`
    : member.profile?.name && member.profile?.last_name
    ? `${member.profile.name} ${member.profile.last_name}`
    : (member.first_name || member.last_name)
    ? `${member.first_name || ''} ${member.last_name || ''}`.trim()
    : member.email;

  // Formatear fecha (evitar problemas de zona horaria con YYYY-MM-DD)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Si viene en formato YYYY-MM-DD, parsear como fecha local
    if (/^\d{4}-\d{2}-\d{2}/.test(String(dateString))) {
      const [y, m, d] = String(dateString).split('T')[0].split('-').map(Number);
      return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const detailTitle = showHistory
    ? 'Historial de movimientos'
    : `Datos del ${isOwner ? 'Propietario' : 'Trabajador'}`;

  const requireMemberInVenue = () => {
    if (isMemberInVenue || isInDispenser) return true;
    window.alert('Este socio no esta dentro del club. Debe tener una entrada activa antes de ir a Dispensador o Productos.');
    return false;
  };

  // Siempre arranca en Movimientos, como la ficha del socio.
  const openHistory = () => {
    setHistoryTab('movimientos');
    setShowHistory(true);
  };

  const goDispenser = () => {
    if (!requireMemberInVenue()) return;
    navigate(`/dispenser?userId=${member.id}`);
    handleModal();
  };

  const openCredit = () => {
    if (onAddCredit) {
      onAddCredit();
      handleModal();
    }
  };

  const openEdit = () => {
    if (onEdit) {
      onEdit();
      handleModal();
    }
  };

  const confirmDelete = () => {
    const confirmMessage = isOwner
      ? '¿Estás seguro de que quieres eliminar este propietario?'
      : '¿Estás seguro de que quieres eliminar este trabajador?';
    if (window.confirm(confirmMessage)) {
      onDelete();
      handleModal();
    }
  };

  const memberRoleLabel = isOwner ? 'propietario' : 'trabajador';
  const actionBtnClass = 'inline-flex items-center justify-center gap-2';
  const mobileActionBtnClass = `${actionBtnClass} w-full min-h-[44px] rounded-xl text-sm font-semibold shadow-sm active:scale-[0.98] transition-transform`;

  return (
    <CustomModal
      active={active}
      handleModal={() => {
        if (showHistory) setShowHistory(false);
        else handleModal();
      }}
      title={detailTitle}
      size="wide"
      titleId="staff-owner-detail-title"
    >
        <div className="wa-form space-y-6">
          {showHistory ? (
            /* Historial de movimientos */
            <div className="min-h-[320px]">
              <div className="mb-4 rounded-xl border border-[var(--wa-border)] bg-[var(--wa-card-muted)] px-4 py-3">
                <p className="text-base font-semibold text-[var(--wa-foreground)]">
                  {member.profile?.first_name || member.profile?.last_name
                    ? `${member.profile?.first_name || ''} ${member.profile?.last_name || ''}`.trim()
                    : (member.first_name || member.last_name)
                    ? `${member.first_name || ''} ${member.last_name || ''}`.trim()
                    : (member.email || 'Personal')}
                </p>
                <p className="mt-1 text-sm text-[var(--wa-muted-foreground)]">
                  Nº Socio: {socioNum(member) ?? '-'}
                </p>
              </div>
              <HistorialPestanas value={historyTab} onChange={setHistoryTab} />
              {historyTab === 'monedero' ? (
                <WalletStatement token={token} venueID={venueID} clientId={member?.id} />
              ) : loadingTransactions ? (
                <div className="flex items-center justify-center py-12">
                  <p className="text-[var(--wa-muted-foreground)]">Cargando transacciones…</p>
                </div>
              ) : (
                <ClientTransactions transactions={transactions} clientId={member?.id} onChanged={fetchTransactions} />
              )}
            </div>
          ) : (
            <>
              {/* Foto y nombre principal */}
              <div className="flex items-center gap-4 border-b pb-4">
                {imageUrl ? (
                  <img 
                    src={imageUrl} 
                    alt={displayName}
                    className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`w-32 h-32 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-bold text-3xl ${imageUrl ? 'hidden' : 'flex'}`}
                >
                  {displayName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">{displayName}</h3>
                  <p className="text-gray-600">{member.email}</p>
                  <p className="text-sm text-gray-500">
                    Nº Socio: {socioNum(member) || '-'} &bull; Tipo: {isOwner ? 'Propietario' : 'Personal'}
                  </p>
                </div>
              </div>

              {/* Datos personales */}
              <div className="wa-form-grid-cols-3">
                <div>
                  <label className="text-sm font-semibold text-gray-600">Nombre</label>
                  <p className="text-gray-800">{member.profile?.first_name || member.first_name || member.profile?.name || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Apellidos</label>
                  <p className="text-gray-800">{member.profile?.last_name || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">DNI</label>
                  <p className="text-gray-800">{member.profile?.national_id || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Fecha de Nacimiento</label>
                  <p className="text-gray-800">{formatDate(member.profile?.birth_date)}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Código Postal</label>
                  <p className="text-gray-800">{member.profile?.postal_code || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Ciudad</label>
                  <p className="text-gray-800">{member.profile?.city || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Dirección</label>
                  <p className="text-gray-800 break-words">{member.profile?.address || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Teléfono</label>
                  <p className="text-gray-800">{member.profile?.phone || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Saldo</label>
                  <p className={`text-lg font-bold ${balanceToneClass(member.profile?.credit_balance, 'text-[var(--foreground)]')}`}>{formatBalance(member.profile?.credit_balance)}</p>
                </div>
              </div>

              {/* Datos de membresía (solo para staff) */}
              {!isOwner && member.profile && (
                <div className="border-t pt-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Datos de Membresía</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Fecha de Inscripción</label>
                      <p className="text-gray-800">{formatDate(member.profile?.enrollment_date)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Fecha de Caducidad</label>
                      <p className="text-gray-800">{formatDate(member.profile?.expiration_date)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Socio Avalado</label>
                      <p className="text-gray-800">{member.profile?.guarantor_member || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Línea de Coste</label>
                      <p className="text-gray-800">{member.profile?.cost_line || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Descuento</label>
                      <p className="text-gray-800">{member.profile?.discount || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Cuota</label>
                      <p className="text-gray-800">{member.profile?.fee || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Consumo Máximo/Mes</label>
                      <p className="text-gray-800">{member.profile?.max_monthly_consumption || 0}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Tipo de Consumo</label>
                      <p className="text-gray-800">
                        {member.profile?.consumption_type?.ludenos ? 'Lúd. ' : ''}
                        {member.profile?.consumption_type?.terap ? 'Terap.' : ''}
                        {!member.profile?.consumption_type?.ludenos && !member.profile?.consumption_type?.terap ? '-' : ''}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Tiendas propias (solo para owners) */}
              {isOwner && member.owned_venues && member.owned_venues.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Tiendas Propias</h4>
                  <div className="space-y-2">
                    {member.owned_venues.map((venue) => (
                      <div key={venue.id} className="bg-gray-50 p-3 rounded">
                        <p className="text-gray-800 font-medium">{venue.name || venue.venue_name || 'Sin nombre'}</p>
                        <p className="text-sm text-gray-500">ID: {venue.id}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Botones de acción */}
              <div className="border-t border-[var(--wa-border)] pt-4">
                {/* Móvil: rejilla táctil */}
                <div className="md:hidden space-y-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 text-center">Acciones rápidas</p>
                  <div className="grid grid-cols-2 gap-2.5">
                    <button type="button" onClick={openHistory} className={`${mobileActionBtnClass} wa-btn-secondary px-4`} title="Ver historial de movimientos">
                      <HiOutlineClock className="text-lg shrink-0" />
                      <span>Historial</span>
                    </button>
                    <button type="button" onClick={openCredit} className={`${mobileActionBtnClass} wa-btn-secondary px-4`} title="Agregar crédito">
                      <MdCreditCard className="text-lg shrink-0" />
                      <span>Crédito</span>
                    </button>
                    <button type="button" onClick={goDispenser} className={`${mobileActionBtnClass} wa-btn-secondary px-4 col-span-2`} title="Ir al dispensador">
                      <BsCart4 className="text-lg shrink-0" />
                      <span>Dispensador</span>
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-2.5 pt-1">
                    <button type="button" onClick={openEdit} className={`${mobileActionBtnClass} wa-btn-primary`} title={`Editar ${memberRoleLabel}`}>
                      <FaEdit className="text-lg shrink-0" />
                      <span>Editar {memberRoleLabel}</span>
                    </button>
                    {onDelete && (
                      <button type="button" onClick={confirmDelete} className={`${mobileActionBtnClass} wa-btn-danger`} title={`Eliminar ${memberRoleLabel}`}>
                        <FaDeleteLeft className="text-lg shrink-0" />
                        <span>Eliminar {memberRoleLabel}</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Tablet / PC: fila flexible (sin cambios) */}
                <div className="hidden md:flex justify-center gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={openHistory}
                    className="wa-btn-secondary inline-flex items-center gap-2 px-4"
                    title="Ver historial de movimientos"
                  >
                    <HiOutlineClock className="text-xl shrink-0" />
                    <span>Historial</span>
                  </button>
                  <button
                    type="button"
                    onClick={goDispenser}
                    className="wa-btn-secondary inline-flex items-center gap-2 px-4"
                    title="Ir al dispensador"
                  >
                    <BsCart4 className="text-xl shrink-0" />
                    <span>Dispensador</span>
                  </button>
                  <button
                    type="button"
                    onClick={openCredit}
                    className="wa-btn-secondary inline-flex items-center gap-2 px-4"
                    title="Agregar crédito"
                  >
                    <MdCreditCard className="text-xl shrink-0" />
                    <span>Crédito</span>
                  </button>
                  <button
                    type="button"
                    onClick={openEdit}
                    className="wa-btn-primary inline-flex items-center gap-2 px-4"
                    title={`Editar ${memberRoleLabel}`}
                  >
                    <FaEdit className="text-xl shrink-0" />
                    <span>Editar</span>
                  </button>
                  {onDelete && (
                    <button
                      type="button"
                      onClick={confirmDelete}
                      className="wa-btn-danger inline-flex items-center gap-2 px-4"
                      title={`Eliminar ${memberRoleLabel}`}
                    >
                      <FaDeleteLeft className="text-xl shrink-0" />
                      <span>Eliminar</span>
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
    </CustomModal>
  );
}

export default StaffOwnerDetailModal;
